import { BRAND_PALETTE, STATUS_COLORS, CHART_COLORS, TYPOGRAPHY_PALETTE } from './palette'

/**
 * INTERAX DESIGN SYSTEM COLORS
 * Colors from the Interax design system - use these everywhere going forward
 */
export const INTERAX_COLORS = {
  // Primary Colors
  offBlack: '#212629',
  heritageBlue: '#2E4F5F',
  thunderGrey: '#78909C',
  thunderGrey20: 'rgba(120, 144, 156, 0.2)', // 20% opacity
  sun: '#FFAB40',

  // Sun Variations
  sun40: 'rgba(255, 171, 64, 0.4)', // 40% opacity
  sun20: 'rgba(255, 171, 64, 0.2)', // 20% opacity
  sun10: 'rgba(255, 171, 64, 0.1)', // 10% opacity

  // Heritage Blue Variations
  heritageBlue30: 'rgba(46, 79, 95, 0.3)', // 30% opacity
  heritageBlue40: 'rgba(46, 79, 95, 0.6)', // 60% opacity for better readability
  heritageBlueRadial:
    'radial-gradient(50.19% 50.19%, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%), rgba(46, 79, 95, 0.5)',

  // White Variations
  white: '#FFFFFF',
  white70: 'rgba(255, 255, 255, 0.7)', // 70% opacity
  white40: 'rgba(255, 255, 255, 0.4)', // 40% opacity
  white30: 'rgba(255, 255, 255, 0.3)', // 30% opacity
  white20: 'rgba(255, 255, 255, 0.2)', // 20% opacity
  white10: 'rgba(255, 255, 255, 0.1)', // 10% opacity
  white04: 'rgba(255, 255, 255, 0.04)', // 4% opacity

  // Black Variations
  blackTransparentBorder: 'rgba(0, 0, 0, 0.6)', // 60% opacity

  // Status Colors
  blue: '#60A5FA',
  blueBackground: 'rgba(59, 130, 246, 0.2)',
  green: '#4ADE80',
  greenBackground: 'rgba(34, 197, 94, 0.2)',
  redError: '#F87171',
  redErrorBackground: 'rgba(239, 68, 68, 0.2)',
  redErrorHover: 'rgba(248, 113, 113, 0.12)', // 12% of redError — destructive row hover

  // Typography Colors
  typographyText: '#FFFFFF',
  typographyLabel: '#FFAB40',
  typographyFont: '#ADB5BD',

  // Gradients
  gradientSun: 'linear-gradient(135deg, #FFDDB3 0%, #FFAB40 100%)',
  gradientSun2: 'linear-gradient(135deg, #FFBC66 100%, #FFCD8C 0%, #FFDDB3 100%)',
  gradientGray: 'linear-gradient(135deg, #FFBC66 100%, #FFCD8C 0%, #FFDDB3 100%)',
  gradientWhite: 'linear-gradient(135deg, #FFFFFF 15%, #FFFFFF 0%, #FFFFFF 0%, #FFFFFF 15%)',
  gradientWhiteSecondary:
    'linear-gradient(319deg, rgba(255, 255, 255, 0.15) 8.79%, rgba(255, 255, 255, 0.00) 34.44%, rgba(255, 255, 255, 0.00) 62.3%, rgba(255, 255, 255, 0.15) 86.83%)',
  gradientWhiteSecondaryHover:
    'linear-gradient(319deg, rgba(255, 255, 255, 0.25) 8.79%, rgba(255, 255, 255, 0.05) 34.44%, rgba(255, 255, 255, 0.05) 62.3%, rgba(255, 255, 255, 0.25) 86.83%)',
  gradientSecondaryGold:
    'linear-gradient(319deg, rgba(255, 255, 255, 0.25) 8.79%, rgba(255, 255, 255, 0.05) 34.44%, rgba(255, 255, 255, 0.05) 62.3%, rgba(255, 255, 255, 0.25) 86.83%)',
  gradientSecondaryGoldHover:
    'linear-gradient(319deg, rgba(255, 255, 255, 0.35) 8.79%, rgba(255, 255, 255, 0.15) 34.44%, rgba(255, 255, 255, 0.15) 62.3%, rgba(255, 255, 255, 0.35) 86.83%)',
  gradientTransparent:
    'linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0) 50%, rgba(255, 255, 255, 1) 100%)',

  // Feedback Generator Gradients
  gradientGoldenTitle: 'linear-gradient(360deg, #FFD196 -15.91%, #FFAB40 1.14%, #FFFFFF 56.35%)',
  teamHealthGreenDark: '#287845',

  // Extended palette for feedback components
  redStrong: '#D55252',
  redSoft: '#F48E8E',
  offWhiteText: '#FFF2F2',
  neutralBorder: '#D1D7E3',
  offBlack80: 'rgba(33, 38, 41, 0.8)',
  white08: 'rgba(255, 255, 255, 0.08)',
  white25: 'rgba(255, 255, 255, 0.25)',
  white15: 'rgba(255, 255, 255, 0.15)',
  white035: 'rgba(255, 255, 255, 0.035)', // 3.5% opacity — barely-there surface tint
  black16: 'rgba(0, 0, 0, 0.16)', // 16% opacity
  black20: 'rgba(0, 0, 0, 0.2)', // 20% opacity — inset panel on a heritage-blue card
  black28: 'rgba(0, 0, 0, 0.28)', // 28% opacity — incoming chat bubble
  black30: 'rgba(0, 0, 0, 0.3)', // 30% opacity — inset video surface
  black55: 'rgba(0, 0, 0, 0.55)', // 55% opacity — floating call controls
  slate: '#263038', // one step lighter than offBlack; panel gradient end
  gradientAssistantPanel: 'linear-gradient(180deg, #212629 0%, #263038 100%)',
  sunDeep: '#E0902F', // darker amber; the outer stop of Aria's orb gradient
  // My people: the card surface, and a result in muted tones — high in sage, middle in silver, low in coral.
  heritageBlueMuted: 'rgba(46, 79, 95, 0.4)', // 40% opacity — the card on the page background
  white60: 'rgba(255, 255, 255, 0.6)', // 60% opacity — captions on such a card
  white80: 'rgba(255, 255, 255, 0.8)', // 80% opacity — labels one step below the headings
  graphite: '#3A3B3D', // empty tracks and thin rules
  graphiteLight: '#44474B', // raised controls and the chip of a middle result
  inkBlue: '#1E2A31', // the hollow of a ring drawn on such a card
  sage: '#5DA283', // a high result: bars, filled marks, dots
  sageLight: '#7FC2A2', // a high result written out
  sageShade: '#4E9A76', // a high result's range bar
  sageDeep: '#2E5A45', // the chip of a high result
  sageMist: '#D3F2E1', // text on a sage chip
  pewter: '#8A8C90', // a middle result's range bar
  silver: '#B8B6B8', // a middle result: filled marks, dots
  silverMist: '#EDEBED', // a middle result written out, and text on its chip
  coral: '#F07F68', // a low result: bars, rings, dots
  coralLight: '#F7A594', // a low result written out
  coralShade: '#B4513F', // a low result's range bar
  coralDeep: '#5A2E26', // the chip of a low result
  coralMist: '#FFD2C8', // text on a coral chip
  sunSoft: '#F1BD6C', // Aria's sparkle and her "Try this"
  sunFaded: 'rgba(255, 171, 64, 0)', // fully transparent sun, so a pulse can fade out to nothing
  gradientAriaOrb: 'radial-gradient(circle at 50% 30%, #FFAB40, #E0902F)',
  // Warm glow behind the voice orb, fading to nothing so it blends into whatever sits under it.
  gradientAssistantVoiceGlow:
    'radial-gradient(120% 120% at 50% 0%, rgba(255, 171, 64, 0.1) 0%, rgba(0, 0, 0, 0) 60%), rgba(255, 255, 255, 0.04)'
} as const

/**
 * Elevation shadows for the floating assistant surfaces. Kept here with the colors so no component
 * hardcodes an rgba shadow inline.
 */
export const INTERAX_SHADOWS = {
  assistantPanel: '0 22px 54px rgba(0, 0, 0, 0.42)',
  assistantBubble: '0 14px 34px rgba(0, 0, 0, 0.34), 0 0 0 8px rgba(255, 171, 64, 0.10)',
  assistantBubbleHover: '0 18px 38px rgba(0, 0, 0, 0.38), 0 0 0 10px rgba(255, 171, 64, 0.14)',
  wherebyFloat: '0 20px 48px rgba(0, 0, 0, 0.65)',
  /** Aria's orb: a soft drop shadow, with a ring added while she is speaking. */
  ariaOrb: '0 6px 20px rgba(0, 0, 0, 0.35)',
  /** Lifts a control hanging off the assistant panel above whatever part of the page it covers. */
  assistantRailButton: '0 2px 8px rgba(0, 0, 0, 0.35)'
} as const

/**
 * INTERAX TYPOGRAPHY STYLES
 * Typography specifications using Lexend Deca font
 */
// Font Family Constants
export const FONT_FAMILY_PRIMARY = 'Lexend Deca, system-ui, -apple-system, sans-serif'
export const FONT_FAMILY_SECONDARY = 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif'

export const INTERAX_TYPOGRAPHY = {
  // Headlines
  headline1: {
    fontFamily: 'Lexend Deca',
    fontWeight: '600', // Semi Bold
    fontSize: '64px',
    lineHeight: '76px'
  },
  headline2: {
    fontFamily: 'Lexend Deca',
    fontWeight: '600', // Semi Bold
    fontSize: '36px',
    lineHeight: '44px'
  },
  headline3: {
    fontFamily: 'Lexend Deca',
    fontWeight: '600', // Semi Bold
    fontSize: '28px',
    lineHeight: '36px'
  },
  headline4: {
    fontFamily: 'Lexend Deca',
    fontWeight: '600', // Semi Bold
    fontSize: '20px',
    lineHeight: '28px'
  },
  headline5: {
    fontFamily: 'Lexend Deca',
    fontWeight: '600', // Semi Bold
    fontSize: '18px',
    lineHeight: '24px'
  },
  headline6: {
    fontFamily: 'Lexend Deca',
    fontWeight: '600', // Semi Bold
    fontSize: '16px',
    lineHeight: '20px'
  },

  // Body Text
  body1: {
    fontFamily: 'Lexend Deca',
    fontWeight: '400', // Regular
    fontSize: '18px',
    lineHeight: '28px'
  },
  body2: {
    fontFamily: 'Lexend Deca',
    fontWeight: '400', // Regular
    fontSize: '16px',
    lineHeight: '24px'
  },
  body3: {
    fontFamily: 'Lexend Deca',
    fontWeight: '400', // Regular
    fontSize: '14px',
    lineHeight: '20px'
  },
  body4: {
    fontFamily: 'Lexend Deca',
    fontWeight: '400', // Regular
    fontSize: '12px',
    lineHeight: '16px'
  },

  // Button/Link Text
  buttonNormal: {
    fontFamily: 'Lexend Deca',
    fontWeight: '500', // Medium
    fontSize: '18px',
    lineHeight: '24px'
  },
  buttonMedium: {
    fontFamily: 'Lexend Deca',
    fontWeight: '500', // Medium
    fontSize: '16px',
    lineHeight: '20px'
  },
  buttonSmall: {
    fontFamily: 'Lexend Deca',
    fontWeight: '500', // Medium
    fontSize: '14px',
    lineHeight: '16px'
  },

  // Field Labels
  fieldLabel: {
    fontFamily: 'Lexend Deca',
    fontWeight: '600', // Semi Bold
    fontSize: '16px',
    lineHeight: '24px'
  },
  fieldText1: {
    fontFamily: 'Lexend Deca',
    fontWeight: '400', // Regular
    fontSize: '16px',
    lineHeight: '24px'
  },
  fieldText2: {
    fontFamily: 'Lexend Deca',
    fontWeight: '400', // Regular
    fontSize: '12px',
    lineHeight: '16px'
  }
} as const

/**
 * TYPOGRAPHY COLORS
 * Typography specifications using Lexend Deca font
 */
export const TYPOGRAPHY_COLORS = {
  // Font specifications
  ...TYPOGRAPHY_PALETTE,

  // Text colors using main palette
  textPrimary: BRAND_PALETTE.offBlack,
  textSecondary: BRAND_PALETTE.black,
  textAccent: BRAND_PALETTE.sun,
  textInverse: BRAND_PALETTE.white,
  textMuted: BRAND_PALETTE.secondary
} as const

/**
 * LANDING PAGE COLORS
 * Colors specific to landing page components
 */
export const LANDING_COLORS = {
  // Navigation
  landingNavBackground: BRAND_PALETTE.primary,
  landingNavBackgroundScrolled: 'rgba(46, 79, 95, 0.95)',
  landingNavBorder: BRAND_PALETTE.secondary,
  landingNavLink: BRAND_PALETTE.accent,
  landingNavText: BRAND_PALETTE.white,
  landingNavigationBackground: 'rgba(46, 79, 95, 0.95)',
  landingNavigationBorder: BRAND_PALETTE.secondary,
  landingNavigationLink: BRAND_PALETTE.accent,

  // Dialog and modals
  landingDialogBackground: BRAND_PALETTE.primary,
  landingDialogBorder: BRAND_PALETTE.secondary,
  landingDialogText: BRAND_PALETTE.white,
  landingDialogTitle: BRAND_PALETTE.accent,
  landingDialogInputBackground: BRAND_PALETTE.secondary,
  landingDialogInputBorder: BRAND_PALETTE.secondary,
  landingDialogInputText: BRAND_PALETTE.dark,

  // Buttons
  landingButtonPrimary: BRAND_PALETTE.accent,
  landingButtonPrimaryText: BRAND_PALETTE.dark,
  landingButtonSecondary: BRAND_PALETTE.secondary,
  landingButtonSecondaryText: BRAND_PALETTE.dark,
  landingButtonDark: BRAND_PALETTE.dark,
  landingButtonDarkText: BRAND_PALETTE.white,

  // Hero section
  landingHeroBackground: BRAND_PALETTE.dark,
  landingHeroGradient: BRAND_PALETTE.heroGradient,
  landingHeroText: BRAND_PALETTE.white,
  landingHeroButtonPrimary: BRAND_PALETTE.primary,
  landingHeroButtonBorder: BRAND_PALETTE.secondary,

  // About section
  landingAboutGradient: BRAND_PALETTE.aboutGradient,
  landingAboutCardBackground: BRAND_PALETTE.secondary,
  landingAboutCardBorder: BRAND_PALETTE.dark,
  landingAboutIconBackground: BRAND_PALETTE.accent,
  landingAboutIconColor: BRAND_PALETTE.dark,
  landingAboutText: BRAND_PALETTE.dark,

  // Business impact
  landingBusinessBackground: BRAND_PALETTE.dark,
  landingBusinessCardBackground: BRAND_PALETTE.businessImpactBg,
  landingBusinessCardBorder: BRAND_PALETTE.secondary,
  landingBusinessIconBackground: BRAND_PALETTE.accent,
  landingBusinessIconColor: BRAND_PALETTE.dark,
  landingBusinessText: BRAND_PALETTE.secondary,
  landingBusinessBadgeBackground: BRAND_PALETTE.accentSuperLight,
  landingBusinessBadgeText: BRAND_PALETTE.accent,

  // Use cases
  landingUseCaseBackground: BRAND_PALETTE.primary,
  landingUseCaseCardBackground: BRAND_PALETTE.primary,
  landingUseCaseCardBorder: BRAND_PALETTE.secondary,
  landingUseCaseIconBackground: BRAND_PALETTE.accent,
  landingUseCaseIconColor: BRAND_PALETTE.dark,

  // Testimonials
  landingTestimonialBackground: BRAND_PALETTE.primary,
  landingTestimonialCardBackground: BRAND_PALETTE.primary,
  landingTestimonialCardBorder: BRAND_PALETTE.secondary,
  landingTestimonialQuote: BRAND_PALETTE.accent,
  landingTestimonialText: BRAND_PALETTE.secondary,
  landingTestimonialNavButton: BRAND_PALETTE.accent,

  // Customer logos
  landingCustomerBackground: BRAND_PALETTE.primary,
  landingCustomerCardBackground: BRAND_PALETTE.secondary,
  landingCustomerCardBorder: BRAND_PALETTE.dark,
  landingCustomerText: BRAND_PALETTE.dark,

  // Contact section
  landingContactBackground: BRAND_PALETTE.dark,
  landingContactText: BRAND_PALETTE.secondary,
  landingContactButtonBackground: BRAND_PALETTE.accent,
  landingContactButtonText: BRAND_PALETTE.dark,

  // Footer
  landingFooterBackground: BRAND_PALETTE.primary,
  landingFooterBorder: BRAND_PALETTE.secondary,
  landingFooterText: BRAND_PALETTE.secondary,

  // Game experience carousel
  landingCarouselBackground: BRAND_PALETTE.dark,
  landingCarouselDotActive: BRAND_PALETTE.accent,
  landingCarouselDotInactive: 'rgba(255, 255, 255, 0.5)'
} as const

/**
 * FACILITATOR COLORS
 * Colors specific to facilitator dashboard and components
 *
 * @deprecated Use INTERAX_COLORS from interaxColors.ts instead.
 * This object is maintained for backward compatibility only.
 * New code should use the design system colors from INTERAX_COLORS.
 */
export const FACILITATOR_COLORS = {
  // Main colors (new specification)
  facilitatorOffBlack: BRAND_PALETTE.offBlack,
  facilitatorSun: BRAND_PALETTE.sun,
  facilitatorAwake: BRAND_PALETTE.awake,
  facilitatorWhite: BRAND_PALETTE.white,
  facilitatorBlack: BRAND_PALETTE.black,

  // Dashboard background
  facilitatorDashboardBackground: BRAND_PALETTE.offBlack,
  facilitatorDashboardCardBackground: BRAND_PALETTE.primary,
  facilitatorDashboardCardBorder: BRAND_PALETTE.secondary,
  facilitatorDashboardText: BRAND_PALETTE.secondary,
  facilitatorDashboardTextWhite: BRAND_PALETTE.white,

  // Sidebar
  facilitatorSidebarBackground: '#222629',
  facilitatorSidebarBorder: BRAND_PALETTE.secondary,
  facilitatorSidebarText: BRAND_PALETTE.white,
  facilitatorSidebarTextMuted: '#text-gray-300',
  facilitatorSidebarHover: 'hover:bg-white/5',

  // Cards and metrics
  facilitatorCardIconBackground: BRAND_PALETTE.accentSuperLight,
  facilitatorCardIconColor: BRAND_PALETTE.sun,
  facilitatorCardText: BRAND_PALETTE.secondary,
  facilitatorCardNumber: BRAND_PALETTE.white,
  facilitatorMetricBackground: BRAND_PALETTE.primary,
  facilitatorMetricBorder: BRAND_PALETTE.secondary,

  // Buttons and actions
  facilitatorButtonPrimary: BRAND_PALETTE.sun,
  facilitatorButtonPrimaryText: BRAND_PALETTE.offBlack,
  facilitatorButtonSecondary: BRAND_PALETTE.primary,
  facilitatorButtonSecondaryBorder: BRAND_PALETTE.secondary,
  facilitatorButtonSecondaryText: BRAND_PALETTE.sun,

  // Wizard steps
  facilitatorWizardBackground: BRAND_PALETTE.primary,
  facilitatorWizardBorder: BRAND_PALETTE.secondary,
  facilitatorWizardCardBackground: BRAND_PALETTE.secondary,
  facilitatorWizardCardBorder: BRAND_PALETTE.offBlack,
  facilitatorWizardText: BRAND_PALETTE.secondary,
  facilitatorWizardTextDark: BRAND_PALETTE.offBlack,
  facilitatorWizardTextWhite: BRAND_PALETTE.white,
  facilitatorWizardIconColor: BRAND_PALETTE.offBlack,
  facilitatorWizardAccentText: BRAND_PALETTE.sun,

  // Wizard steps - specific components
  facilitatorWizardStepBackground: BRAND_PALETTE.primary,
  facilitatorWizardStepBorder: BRAND_PALETTE.secondary,
  facilitatorWizardStepCardBackground: BRAND_PALETTE.secondary,
  facilitatorWizardStepCardBorder: BRAND_PALETTE.offBlack,
  facilitatorWizardStepTextSecondary: BRAND_PALETTE.secondary,
  facilitatorWizardStepTextDark: BRAND_PALETTE.offBlack,
  facilitatorWizardStepIconDark: BRAND_PALETTE.offBlack,

  // Wizard buttons
  facilitatorWizardButtonPrimary: BRAND_PALETTE.sun,
  facilitatorWizardButtonPrimaryText: BRAND_PALETTE.offBlack,
  facilitatorWizardButtonSecondary: BRAND_PALETTE.primary,
  facilitatorWizardButtonSecondaryBorder: BRAND_PALETTE.sun,

  // Review and customize specific
  facilitatorReviewSectionBackground: BRAND_PALETTE.primary,
  facilitatorReviewSectionText: BRAND_PALETTE.secondary,
  facilitatorReviewAccentText: BRAND_PALETTE.sun,
  facilitatorReviewCardBackground: BRAND_PALETTE.secondary,
  facilitatorReviewCardText: BRAND_PALETTE.offBlack,
  facilitatorReviewPreviewBackground: BRAND_PALETTE.offBlack,
  facilitatorReviewPreviewText: BRAND_PALETTE.white,

  // Charts and data visualization
  facilitatorChartTeam: BRAND_PALETTE.sun,
  facilitatorChartNorm: BRAND_PALETTE.secondary,
  facilitatorChartPlanning: BRAND_PALETTE.primary,
  facilitatorChartExecution: BRAND_PALETTE.sun,
  facilitatorChartEvaluation: BRAND_PALETTE.secondary,
  facilitatorChartQuestions: BRAND_PALETTE.sun,
  facilitatorChartResources: BRAND_PALETTE.secondary,
  facilitatorChartAxisText: BRAND_PALETTE.white,
  facilitatorChartBackground: BRAND_PALETTE.primary,

  // Form elements
  facilitatorFormBackground: BRAND_PALETTE.secondary,
  facilitatorFormBorder: BRAND_PALETTE.secondary,
  facilitatorFormText: BRAND_PALETTE.offBlack,
  facilitatorFormLabel: BRAND_PALETTE.white,

  // Sessions and teams
  facilitatorSessionCard: BRAND_PALETTE.primary,
  facilitatorSessionBorder: BRAND_PALETTE.secondary,
  facilitatorTeamCard: BRAND_PALETTE.secondary,
  facilitatorTeamBorder: BRAND_PALETTE.offBlack,
  facilitatorTeamIconBackground: BRAND_PALETTE.sun,

  // Training sprint management
  facilitatorSprintBackground: BRAND_PALETTE.primary,
  facilitatorSprintBorder: BRAND_PALETTE.secondary,
  facilitatorSprintText: BRAND_PALETTE.secondary,
  facilitatorSprintAccent: BRAND_PALETTE.sun
} as const

/**
 * GAMER COLORS
 * Colors specific to gamer dashboard and gaming components
 *
 * @deprecated Use INTERAX_COLORS from interaxColors.ts instead.
 * This object is maintained for backward compatibility only.
 * New code should use the design system colors from INTERAX_COLORS.
 */
export const GAMER_COLORS = {
  // Page-level backgrounds
  gamerPageBackground: 'rgb(33, 38, 41)',
  gamerSectionBackground: 'rgb(46, 79, 95)',
  gamerCardBackground: 'rgb(120, 144, 156)',

  // Navigation
  gamerNavBackground: 'rgb(46, 79, 95)',
  gamerNavBorder: 'rgb(120, 144, 156)',
  gamerNavText: BRAND_PALETTE.white,
  gamerNavButtonBorder: 'rgb(120, 144, 156)',
  gamerNavButtonText: BRAND_PALETTE.accent,

  // Dashboard
  gamerDashboardBackground: 'rgb(33, 38, 41)',
  gamerDashboardText: BRAND_PALETTE.white,
  gamerDashboardButtonBackground: BRAND_PALETTE.accent,
  gamerDashboardButtonText: 'rgb(33, 38, 41)',

  // Dialog and modals
  gamerDialogBackground: 'rgb(46, 79, 95)',
  gamerDialogBorder: 'rgb(120, 144, 156)',
  gamerDialogTitle: BRAND_PALETTE.accent,
  gamerDialogText: BRAND_PALETTE.white,
  gamerDialogInputBackground: 'rgb(120, 144, 156)',
  gamerDialogInputBorder: 'rgb(120, 144, 156)',
  gamerDialogInputText: 'rgb(33, 38, 41)',

  // Buttons
  gamerButtonPrimary: BRAND_PALETTE.accent,
  gamerButtonPrimaryText: 'rgb(33, 38, 41)',
  gamerButtonOutline: 'transparent',
  gamerButtonOutlineBorder: 'rgb(120, 144, 156)',
  gamerButtonOutlineText: BRAND_PALETTE.accent,

  // Card content text colors
  gamerCardText: 'rgb(33, 38, 41)',
  gamerCardTextLight: BRAND_PALETTE.white,

  // Profile components
  gamerProfileCardBackground: BRAND_PALETTE.primary,
  gamerProfileCardBorder: BRAND_PALETTE.secondary,
  gamerProfileIconColor: BRAND_PALETTE.accent,
  gamerProfileIconBackground: BRAND_PALETTE.accent,
  gamerProfileText: BRAND_PALETTE.dark,
  gamerProfileTextSecondary: BRAND_PALETTE.secondary,
  gamerProfileBadgeBackground: BRAND_PALETTE.accentSuperLight,
  gamerProfileBadgeText: BRAND_PALETTE.accent,

  // Personality traits
  gamerPersonalityIconColor: BRAND_PALETTE.accent,
  gamerPersonalityBarBackground: BRAND_PALETTE.secondaryAlpha30,
  gamerPersonalityBarFill: BRAND_PALETTE.accent,
  gamerPersonalityText: BRAND_PALETTE.secondary,

  // Behavioral components
  gamerBehavioralTooltipBackground: BRAND_PALETTE.secondaryAlpha10,
  gamerBehavioralTooltipBorder: BRAND_PALETTE.secondaryAlpha30,
  gamerBehavioralTooltipText: BRAND_PALETTE.accent,
  gamerBehavioralCardBackground: BRAND_PALETTE.primary,
  gamerBehavioralCardBorder: BRAND_PALETTE.secondary,

  // Feedback components
  gamerFeedbackCardBackground: BRAND_PALETTE.primary,
  gamerFeedbackCardBorder: BRAND_PALETTE.secondary,
  gamerFeedbackIconColor: BRAND_PALETTE.accent,
  gamerFeedbackAccentText: BRAND_PALETTE.accent,
  gamerFeedbackButtonBackground: BRAND_PALETTE.accent,
  gamerFeedbackButtonText: BRAND_PALETTE.dark,
  gamerFeedbackTooltipBackground: BRAND_PALETTE.secondaryAlpha10,
  gamerFeedbackTooltipBorder: BRAND_PALETTE.secondaryAlpha30,

  // Charts and metrics
  gamerChartPerformance: BRAND_PALETTE.accent,
  gamerChartAvailable: BRAND_PALETTE.secondary,
  gamerChartUsed: BRAND_PALETTE.accent,
  gamerChartAxisText: '#9CA3AF',
  gamerChartBackground: BRAND_PALETTE.primary,
  gamerChartStroke: BRAND_PALETTE.secondary,

  // Experience carousel
  gamerExperienceHighlight: BRAND_PALETTE.accent,
  gamerExperienceCard: BRAND_PALETTE.primary,
  gamerExperienceBorder: BRAND_PALETTE.secondary,

  // Communication components
  gamerCommunicationColors: ['#FFAB40', '#10B981', '#3B82F6', '#8B5CF6'],
  gamerCommunicationButtonActive: BRAND_PALETTE.accent,
  gamerCommunicationButtonActiveText: BRAND_PALETTE.dark,

  // Heatmap visualization
  gamerHeatmapIntense: 'rgba(255, 171, 64, 0.2)',
  gamerHeatmapMedium: 'rgba(16, 185, 129, 0.2)',
  gamerHeatmapTextLight: '#fff',
  gamerHeatmapTextDark: '#000',

  // Session summaries
  gamerSessionButtonBackground: BRAND_PALETTE.accent,
  gamerSessionButtonText: BRAND_PALETTE.dark,
  gamerSessionHighlight: BRAND_PALETTE.accent
} as const

/**
 * PARTNER COLORS
 * Colors specific to partner dashboard (uses same palette as facilitator but with slight variations)
 */
export const PARTNER_COLORS = {
  // Use facilitator colors as base since partner page follows same design patterns
  ...FACILITATOR_COLORS,

  // Partner-specific overrides
  partnerDashboardBackground: BRAND_PALETTE.dark,
  partnerCardBackground: BRAND_PALETTE.primary,
  partnerCardBorder: BRAND_PALETTE.secondary,
  partnerIconColor: BRAND_PALETTE.accent,
  partnerText: BRAND_PALETTE.secondary,
  partnerTextWhite: BRAND_PALETTE.white,
  partnerTextDark: BRAND_PALETTE.dark,
  partnerButtonPrimary: BRAND_PALETTE.accent,
  partnerButtonPrimaryText: BRAND_PALETTE.dark,
  partnerFacilitatorCardBackground: BRAND_PALETTE.secondary,
  partnerFacilitatorCardBorder: BRAND_PALETTE.dark,
  partnerFacilitatorIconBackground: BRAND_PALETTE.accent
} as const

/**
 * SHARED UI COLORS
 * Colors used across multiple features for UI components
 */
export const UI_COLORS = {
  // Error boundary
  errorBoundaryBackground: BRAND_PALETTE.dark,
  errorBoundaryCardBackground: 'bg-red-100',
  errorBoundaryIconColor: 'text-red-600',
  errorBoundaryButtonBackground: BRAND_PALETTE.primary,

  // Toast notifications
  toastBackground: BRAND_PALETTE.primary,
  toastText: BRAND_PALETTE.white,
  toastSuccessBackground: BRAND_PALETTE.accent,
  toastSuccessText: BRAND_PALETTE.dark,

  // Dialogs and modals
  dialogBackground: BRAND_PALETTE.primary,
  dialogBorder: BRAND_PALETTE.secondary,
  dialogTitle: BRAND_PALETTE.accent,
  dialogLabel: BRAND_PALETTE.white,
  dialogInputBackground: BRAND_PALETTE.secondary,
  dialogInputBorder: BRAND_PALETTE.secondary,
  dialogInputText: BRAND_PALETTE.dark,
  dialogButtonPrimary: BRAND_PALETTE.accent,
  dialogButtonPrimaryText: BRAND_PALETTE.dark,

  // Form elements
  inputBackground: BRAND_PALETTE.secondary,
  inputBorder: BRAND_PALETTE.secondary,
  inputText: BRAND_PALETTE.dark,
  labelText: BRAND_PALETTE.white,

  // Status indicators
  statusSuccess: STATUS_COLORS.success,
  statusWarning: STATUS_COLORS.warning,
  statusError: STATUS_COLORS.error,
  statusInfo: STATUS_COLORS.info,

  // Flow components
  flowErrorBackground: 'rgba(239, 68, 68, 0.1)',
  flowErrorBorder: '#ef4444',
  flowErrorText: '#ef4444',
  flowErrorIcon: '#ef4444',
  flowSuccessBackground: 'rgba(34, 197, 94, 0.1)',
  flowSuccessBorder: '#22c55e',
  flowSuccessText: '#22c55e',
  flowWarningBackground: 'rgba(245, 158, 11, 0.1)',
  flowWarningBorder: '#f59e0b',
  flowWarningText: '#f59e0b',
  flowInfoBackground: 'rgba(59, 130, 246, 0.1)',
  flowInfoBorder: '#3b82f6',
  flowInfoText: '#3b82f6'
} as const

// Export all color constants
export const COLORS = {
  ...INTERAX_COLORS,
  ...INTERAX_TYPOGRAPHY,
  ...LANDING_COLORS,
  ...FACILITATOR_COLORS,
  ...GAMER_COLORS,
  ...PARTNER_COLORS,
  ...UI_COLORS,
  ...STATUS_COLORS,
  ...CHART_COLORS,
  ...BRAND_PALETTE
} as const

// Export by feature for easier imports
export const FEATURE_COLORS = {
  landing: LANDING_COLORS,
  facilitator: FACILITATOR_COLORS,
  gamer: GAMER_COLORS,
  partner: PARTNER_COLORS,
  ui: UI_COLORS
} as const
